import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, map, Observable, Subject, switchMap, timer } from 'rxjs';
import { AssessmentService } from 'src/app/services/assessment.service';
import { AuthService } from 'src/app/services/auth.service';
import { ProjectService } from 'src/app/services/project.service';
import { CreateAssessmentComponent } from 'src/app/shared/components/create-assessment/create-assessment.component';
import { Assessment } from 'src/app/shared/models/assessment.model';
import { Project } from 'src/app/shared/models/project.model';
import { ViewAssessmentTeamComponent } from './view-assessment-team/view-assessment-team.component';

@Component({
    selector: 'app-assessment-page',
    templateUrl: './assessment-page.component.html',
    styleUrl: './assessment-page.component.scss',
    standalone: false
})
export class AssessmentPageComponent implements OnInit, OnDestroy {
  @ViewChild('createAssessmentModal') createAssessmentModal!: CreateAssessmentComponent;
  @ViewChild('assessmentTeam') assessmentTeam!: ViewAssessmentTeamComponent;
  @ViewChild('editDetailsModal') editDetailsModal!: TemplateRef<any>;
  @ViewChild('deleteActivityModal') deleteActivityModal!: TemplateRef<any>;
  @ViewChild('successModal') successModal!: TemplateRef<any>;
  @ViewChild('statusModal') statusModal!: TemplateRef<any>;

  private searchSubject = new Subject<string>();
  createForm!: UntypedFormGroup;

  assessmentID!: number;
  projectId!: number;
  assessment!: Assessment;
  private originalFormValues: any = {};
  allActivities: any[] = [];
  recentActivities: any[]= [];

  isRefreshDisabled = false;
  isRefreshing = false;

  selectedActivity!: any;
  editDetailsModalRef: any;
  deleteModalRef: any;

  colspan = 0;

  loadingPageData = false;
  loading = false;
  searchText: string = '';

  currentUser: any = null;
	current_client: any = null;

  allowedRoles = ['OWNER', 'EDITOR', 'ANALYST', 'RESTRICTED'];
  allowSpecial = false;

  isOwner = false;
	isEditor = false;
	isAnalyst = false;
	isAllow = false;

  submitted = false;
	spinner = false;
	success = false;
	message = '';
	title = '';

  currenttab = 1;

  currentPage: number = 1;
  pageSize: number = 7;
  totalActivities: number = 0;
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

	startIndex: number = 0;
  endIndex: number = 0;

	conversations: number = 0;
  examinees: number = 0;

  listStatus: any[] = [
		{ name: 'WAITING', status_color: 'warning'},
		{ name: 'PAUSED', status_color: 'warning'},
		{ name: 'STARTED', status_color: 'info'},
		{ name: 'ANALYZING', status_color: 'info'},
		{ name: 'COMPLETED', status_color: 'success'},
		{ name: 'FAILED', status_color: 'danger'}
	];

  newCaseId = null;
  newItemId = null;
  newItemType = null;
  actionType: String | null = null;

  modalStatusTitle = '';
  modalStatusMessage = '';
  modalStatusIcon = '';
  modalStatusIconTrigger = 'loop';
  modalStatusIconColors = '';

  constructor(private route: ActivatedRoute, private router: Router, private fb: UntypedFormBuilder, private projectService: ProjectService, private assessmentService: AssessmentService, private authService: AuthService, private modalService: NgbModal) {}

  ngOnInit(): void {
		this.loading = true;
		this.projectService.setLeftSideBar(true);

    this.currentUser = this.authService.currentUserValue;
    this.current_client = this.authService.currentClientValue;

		this.route.params.subscribe((params: any) => {
			this.assessmentID = params.assessmentid;
			this.projectId = params.projectid;
      
      const initialQueryParams = this.route.snapshot.queryParams;
      this.currenttab = this.returnCurrentTab(initialQueryParams['tab']);

      this.getDetails();
      this.route.queryParams.subscribe((queryParams: any) => {
        this.currenttab = this.returnCurrentTab(queryParams.tab);
      });
		});

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.getDetails();
    });
	}

  ngOnDestroy(): void {
		this.projectService.setLeftSideBar(false);
	}

  getDetails() {
    this.isOwner = false;
    this.isEditor = false;
    this.isAnalyst = false;

    this.loadingPageData = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

    this.assessmentService.getProjectAssessment(this.projectId, this.assessmentID, params).subscribe({
      next: (response) => {
        this.assessment = response.case_data;

        const currentUserMembership = this.assessment.case_members?.find(
          (member: any) => member.client_user_id === this.current_client.id
        );

        if (currentUserMembership && currentUserMembership.roles) {
          this.isEditor = currentUserMembership.roles.includes('EDITOR');
          this.isAnalyst = currentUserMembership.roles.includes('ANALYST');
        }

        this.isOwner = this.current_client.id === this.assessment.case_owner_id;
        this.isAllow = this.isOwner || this.isEditor || this.isAnalyst;
        this.colspan = this.isAllow ? 9 : 8;

        this.allActivities = response.activities || [];
        this.recentActivities = response.recentActivities;
        this.totalActivities = response.totalActivities;
        this.conversations = response.conversationsCount;
        this.examinees = response.examineesCount;
        this.calculateIndexes();

        this.loading = false;
        this.loadingPageData = false;
        this.isRefreshing = false;
      },
      error: (err) => {
        this.authService.toastErrorMessage(err.message ? err.message : err, 'Fetching Error');
        this.goBack();
      }
    });
  }

  calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalActivities);
  }

  openReInviteModal(activity: any) {
    this.message = '';
    this.title = '';
    this.createAssessmentModal.openModal(this.projectId, this.assessment, true, activity);
  }

  openCreateAssessmentModal() {
    this.message = '';
    this.title = '';
    this.createAssessmentModal.openModal(this.projectId, this.assessment);
	}

  get f() { return this.createForm.controls; }

  openEditDetailsModal() {
    this.submitted = false;
    this.spinner = false;
    this.success = false;
    this.message = '';
    this.title = '';
    this.actionType = 'edit_details';

    if (this.assessment) {
      this.originalFormValues = {
        subject_id: this.assessment.subject_id,
        first_name: this.assessment.subject_details.first_name,
        last_name: this.assessment.subject_details.last_name
      };

      this.createForm = this.fb.group({
        subject_id: [this.assessment.subject_id, [Validators.required], [this.existingIdValidator(this.assessment.subject_id)]],
        first_name: [this.assessment.subject_details.first_name, Validators.required],
        last_name: [this.assessment.subject_details.last_name, Validators.required]
      });

      this.editDetailsModalRef = this.modalService.open(this.editDetailsModal, { size: 'l', backdrop: 'static', keyboard : false });
    } else {
      this.authService.toastErrorMessage('User Not found', '')
    }
	}

  existingIdValidator(originalId: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return timer(400).pipe(
        switchMap(() => {
          return this.assessmentService.checkIdExists(this.projectId, control.value, originalId).pipe(
            map(idExists => (idExists ? { idExists: true } : null))
          );
        })
      );
    };
  }

  refreshActivities(bypassCooldown: boolean = false) {
    if (!bypassCooldown && (this.isRefreshDisabled || this.loadingPageData)) {
      return;
    }

    if (!bypassCooldown) {
      this.isRefreshDisabled = true;
      this.isRefreshing = true;
      setTimeout(() => {
        this.isRefreshDisabled = false;
      }, 5000);
    }

    this.currentPage = 1;
    this.searchText = '';
    this.getDetails();
  }

  afterAssessmentCreation(data: any) {
    this.success = true;
    this.message = data.message;

    // CHECK: Is this a Re-Invite or a Creation?
    if (data.action === 're-invite') {
      this.actionType = 're-invite'; // Set a specific action type
      this.title = 'Invitation Re-sent Successfully';
      
      // Clear these as they aren't needed/provided for re-invite
      this.newCaseId = null;
      this.newItemId = null;
      this.newItemType = null;
    } else {
      // Standard Creation Flow
      this.actionType = 'create';
      this.title = 'Assessment creation Success.';
      this.newCaseId = data.caseId;
      this.newItemId = data.newItemId;
      this.newItemType = data.newItemType;
    }

    this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
  }

  onSubmitEditDetails() {
		this.submitted = true;
		this.spinner = true;

    const allValues = this.createForm.getRawValue();
    const changedValues: Record<string, any> = {};
    const controls = this.createForm.controls;

    for (const key in controls) {
      if (controls.hasOwnProperty(key)) {
        const control = controls[key];

        if (control.value !== this.originalFormValues[key]) {
          changedValues[key] = control.value;
        }
      }
    }

    this.assessmentService.updateAssessment(this.assessment.project_id, this.assessment.id, changedValues).subscribe({
      next: (res) => {
        this.submitted = false;
        this.spinner = false;
        this.success = true;
        this.title = 'Update Details Success.';
        this.message = res.message;
        this.assessment = res.case_data;
        this.editDetailsModalRef.close();
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });        
      },
      error: (err) => {
        this.submitted = false;
        this.spinner = false;
        this.success = false;
        this.title = 'Update Details Error.';
        this.message = err.message || 'Error Updating Details!';
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });  
      }
    });

  }

  changeTab(tabName: string) {
		if (tabName === 'overview') {
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { }
			});
		} else {
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { tab: tabName },
				queryParamsHandling: 'merge'
			});
		}
	}

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

  returnCurrentTab(tab: string): number {
		switch (tab) {
			case 'overview':
				return 1;	
			case 'assessments':
				return 2;
			case 'team':
				return 3;
			default:
				return 1;
		}
	}

  getStatusColor(status: string) {
		const s = this.listStatus.find(item => item.name === status);
		return s.status_color;
	}

  getAnalyzingPrecent(ai_models: any) {
		const active_models = Object.values(ai_models).filter((obj: any) => obj.active);
		const done_models = Object.values(active_models).filter((obj: any) => obj.is_done || obj.is_error).length;
		return `${((done_models/active_models.length) * 100).toFixed(0)}%`;
	}

  goBack() {
		this.router.navigate(['/projects/' + this.projectId], { queryParams: { 'tab': 'assessments' } });
	}

  openDeleteActivityModal(activity: any) {
    this.message = '';
    this.title = '';
    this.selectedActivity = activity;
    this.actionType = 'delete';
    this.deleteModalRef = this.modalService.open(this.deleteActivityModal, { size: 'l', backdrop: 'static', keyboard : false });
  }

  deleteActivity() {
    this.loadingPageData = true;
    this.submitted = true;
    this.spinner = true;

    this.assessmentService.deleteProjectCaseActivity(this.projectId, this.assessmentID, this.selectedActivity.id, { activity_type: this.selectedActivity.type }).subscribe({
      next: (res) => {
        this.success = true;
        this.title = 'Delete Activity Success.';
        this.submitted = false;
        this.spinner = false;
        this.message = res.message;
        this.deleteModalRef.close();
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      },
      error: (err) => {
        this.submitted = false;
        this.spinner = false;
        this.success = false;
        this.title = 'Error deleting the activity!';
        this.message = err.message;
        this.deleteModalRef.close();
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      }
    });

    
  }

  closeModal() {
		if (this.success) {
      switch (this.actionType) {
        case 'create':
          if (this.newItemType === 'conversation') {
            this.router.navigate(['/projects/' + this.projectId + '/assessment/' + this.newCaseId + '/conversation/' + this.newItemId]);
          } else {
            this.refreshActivities(true);
          }
          break;
        case 'edit_details':
          break;
        default:
          this.refreshActivities(true);
          break;
      }

			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

  onViewActivity(activity: any) {
    switch (activity.status) {
      case 'COMPLETED':
        this.navigateToActivityPage(activity);
        break;

      case 'WAITING':
        if (activity.type === 'conversation') {
          this.navigateToActivityPage(activity);
        } else {
          this.openStatusModal(activity);
        }
        break;

      // These statuses all show the modal
      case 'STARTED':
      case 'ANALYZING':
      case 'FAILED':
        this.openStatusModal(activity);
        break;

      default:
        this.openStatusModal(activity);
    }
  }

  private getStatusDetails(activity: any): { title: string, message: string, icon: string, trigger: string, colors: string } {
    const status = activity.status;
    
    if (activity.type === 'examinee') {
      // --- Examinee Statuses ---
      switch (status) {
        case 'WAITING':
          return {
            title: 'Assessment Waiting',
            message: 'This activity is waiting for the person to start the questionnaire.',
            icon: 'https://cdn.lordicon.com/ejxwvtlg.json', // Hourglass
            trigger: 'loop',
            colors: 'primary:#6c757d,secondary:#e8e8e8'
          };
        case 'STARTED':
          return {
            title: 'Assessment In Progress',
            message: 'The person has started the questionnaire and is currently answering the questions.',
            icon: 'https://cdn.lordicon.com/ynooheet.json', // Play
            trigger: 'loop',
            colors: 'primary:#0ab39c,secondary:#0ab39c'
          };
        case 'ANALYZING':
          return {
            title: 'Analysis in Progress',
            message: 'The person has finished the questionnaire. The system is now processing and analyzing the recorded video and answers.',
            icon: 'https://cdn.lordicon.com/qhgmphtg.json', // Gears
            trigger: 'loop',
            colors: 'primary:#405189,secondary:#6c757d'
          };
        case 'FAILED':
          return {
            title: 'Analysis Failed',
            message: 'Something went wrong during the analysis or while the person was answering the questionnaire. You may need to delete this activity and create a new one.',
            icon: 'https://cdn.lordicon.com/tdrtiskw.json', // Error
            trigger: 'loop',
            colors: 'primary:#f06548,secondary:#f7b84b'
          };
      }
    } else {
      // --- Conversation Statuses ---
      switch (status) {
        case 'STARTED':
          return {
            title: 'Session in Progress',
            message: 'This conversation session is currently being recorded and is visible to other authorized users.',
            icon: 'https://cdn.lordicon.com/ynooheet.json', // Play
            trigger: 'loop',
            colors: 'primary:#0ab39c,secondary:#0ab39c'
          };
        case 'ANALYZING':
          return {
            title: 'Analysis in Progress',
            message: 'The conversation has finished. The system is now processing and analyzing the recorded video.',
            icon: 'https://cdn.lordicon.com/qhgmphtg.json', // Gears
            trigger: 'loop',
            colors: 'primary:#405189,secondary:#6c757d'
          };
        case 'FAILED':
          return {
            title: 'Analysis Failed',
            message: 'Something went wrong during the analysis of the conversation. You may need to delete this activity and try again.',
            icon: 'https://cdn.lordicon.com/tdrtiskw.json', // Error
            trigger: 'loop',
            colors: 'primary:#f06548,secondary:#f7b84b'
          };
      }
    }
    
    // Fallback for any unhandled combination
    return { 
      title: status, 
      message: 'No details available for this status.',
      icon: 'https://cdn.lordicon.com/jxwksgwv.json', // Info
      trigger: 'loop',
      colors: 'primary:#6c757d,secondary:#e8e8e8'
    };
  }

  private openStatusModal(activity: any) {
    const details = this.getStatusDetails(activity);
    this.modalStatusTitle = details.title;
    this.modalStatusMessage = details.message;
    this.modalStatusIcon = details.icon;
    this.modalStatusIconTrigger = details.trigger;
    this.modalStatusIconColors = details.colors;
    this.modalService.open(this.statusModal, { size: 'l', backdrop: 'static', keyboard : false });
  }

  private navigateToActivityPage(activity: any) {
    const route = `/projects/${this.projectId}/assessment/${this.assessmentID}/${activity.type}/${activity.id}`;
    this.router.navigate([route]);
  }

}
